
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute } from 'vue-router'

// Components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'

// module components
import History from '@/modules/SecurityCheck/components/History/index.vue'

// Types
import { CouirerCarT } from '@/core/types/couriers.types'

// Realisation
import { couriers } from '@/core/realisations/couriers.realisation'

// Composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

// Validations
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

export default defineComponent({
    components: {
        PageTitle,
        Loader,
        TheButton,
        FileReader,
        Cheap,
        History,
    },
    setup() {
        const route = useRoute()
        const car_id = Number(route.params.car_id as string)

        const notifications = useNotifications()
        const { havePermissions } = useAccount()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            car: null as CouirerCarT | null,
            is_status_change_loading: false,
        })

        const loadCar = async () => {
            try {
                state.is_loading = true

                const response = await couriers.getCar(car_id)
                state.car = response.data

                state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении данных автомобиля прозиошла ошибка',
                })
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(loadCar)

        const upload_state = reactive({
            technical_passport: [] as File[],
            is_technical_passport_loading: false,
        })

        const upload_state$ = useVuelidate(
            {
                technical_passport: { required },
            },
            upload_state
        )

        const isCanUploadDocuments = computed(
            () =>
                Boolean(state.car) &&
                havePermissions(['cars-upload-documents', 'all-root-permission'])
        )

        const uploadTechnicalPassport = async () => {
            upload_state$.value.technical_passport.$touch()
            if (upload_state$.value.technical_passport.$invalid) return

            try {
                upload_state.is_technical_passport_loading = true

                const form_data = new FormData()
                form_data.append('type', '12')

                upload_state.technical_passport.forEach((technical_passport) => {
                    form_data.append('files[]', technical_passport)
                })

                await couriers.uploadCarDocument(car_id, form_data)

                notifications.notify({ title: 'Фото тех. паспорта загружены', type: 'success' })
                loadCar()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке фото тех. паспорта произошла ошибка',
                })
            } finally {
                upload_state.is_technical_passport_loading = false
            }
        }

        const isCarOnCheck = computed(() => {
            return state.car && state.car.status.id === 5
        })

        const isCarActive = computed(() => {
            return state.car && state.car.status.id === 6
        })

        const isCanSetActiveStatus = computed(() => {
            return havePermissions(['cabinet-cars-status-active', 'all-root-permission'])
        })

        const isCarArchive = computed(() => {
            return state.car && state.car.status.id === 7
        })

        const isCanSetArchiveStatus = computed(() => {
            return havePermissions(['cabinet-cars-status-archived', 'all-root-permission'])
        })

        const isCarReject = computed(() => {
            return state.car && state.car.status.id === 8
        })

        const isCanSetRejectStatus = computed(() => {
            return havePermissions(['cabinet-cars-status-reject', 'all-root-permission'])
        })

        const isRenderActiveStatusButton = computed(
            () =>
                Boolean(state.car) &&
                isCanSetActiveStatus.value &&
                (isCarOnCheck.value || isCarArchive.value)
        )
        const isRenderArchiveStatusButton = computed(
            () => Boolean(state.car) && isCanSetArchiveStatus.value && isCarActive.value
        )
        const isRenderRejectStatusButton = computed(
            () => Boolean(state.car) && isCanSetRejectStatus.value && isCarOnCheck.value
        )

        const updateStatus = (status_id: number, status_name: string) => {
            if (!state.car) return

            state.car.status.id = status_id
            state.car.status.name = status_name
        }

        const setActiveStatus = async () => {
            if (state.is_status_change_loading || !state.car) return

            try {
                state.is_status_change_loading = true

                await couriers.changeCarStatus({
                    carId: car_id,
                    statusId: 6,
                })

                updateStatus(6, 'Активный')

                notifications.notify({ title: 'Статус изменен', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При смене статуса произошла ошибка' })
            } finally {
                state.is_status_change_loading = false
            }
        }

        const setArchiveStatus = async () => {
            if (state.is_status_change_loading || !state.car) return

            try {
                state.is_status_change_loading = true

                await couriers.changeCarStatus({
                    carId: car_id,
                    statusId: 7,
                })

                updateStatus(7, 'В архиве')

                notifications.notify({ title: 'Статус изменен', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При смене статуса произошла ошибка' })
            } finally {
                state.is_status_change_loading = false
            }
        }

        const setRejectStatus = async () => {
            if (state.is_status_change_loading || !state.car) return

            try {
                state.is_status_change_loading = true

                await couriers.changeCarStatus({
                    carId: car_id,
                    statusId: 8,
                })

                updateStatus(8, 'Отказ СБ')

                notifications.notify({ title: 'Статус изменен', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При смене статуса произошла ошибка' })
            } finally {
                state.is_status_change_loading = false
            }
        }

        const isHistory = computed(() => {
            return state.car && state.car.number
        })

        return {
            state,
            loadCar,
            upload_state,
            upload_state$,
            isCanUploadDocuments,
            uploadTechnicalPassport,
            //
            isCarOnCheck,
            isCarActive,
            isCarArchive,
            isCarReject,
            //
            isRenderActiveStatusButton,
            isRenderArchiveStatusButton,
            isRenderRejectStatusButton,
            //
            setActiveStatus,
            setArchiveStatus,
            setRejectStatus,
            //
            isHistory,
        }
    },
})
